import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiEdit2,
} from 'react-icons/fi';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface Partner {
  id: string;
  name: string;
  logoUrl: string;
  redirectUrl: string | null;
  position: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const Partners: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    redirectUrl: '',
    position: 0,
    active: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const fetchPartners = React.useCallback(async () => {
    try {
      const res = await api.get('/partners/admin');
      setPartners(res.data.partners || []);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchPartners();
  }, [navigate, fetchPartners]);

  const handleOpenModal = (partner?: Partner) => {
    setLogoFile(null);
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name,
        logoUrl: partner.logoUrl,
        redirectUrl: partner.redirectUrl || '',
        position: partner.position,
        active: partner.active,
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        logoUrl: '',
        redirectUrl: '',
        position: partners.length,
        active: true,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      let logoUrl = formData.logoUrl;

      // Upload new logo if file is selected
      if (logoFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('logo', logoFile);

        const uploadRes = await api.post('/upload/logo', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        logoUrl = uploadRes.data.logoUrl;
      }

      // Save partner
      const partnerData = { ...formData, logoUrl };
      if (editingPartner) {
        await api.put(`/partners/${editingPartner.id}`, partnerData);
      } else {
        await api.post('/partners', partnerData);
      }

      setShowModal(false);
      setLogoFile(null);
      fetchPartners();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save partner');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await api.delete(`/partners/${id}`);
      fetchPartners();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete partner');
    }
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.375rem' }}>Gestion des Partenaires</h1>
        <button
          onClick={() => handleOpenModal()}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          <FiPlus size={20} />
          Ajouter un partenaire
        </button>
      </div>

        {/* Partners List */}
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2>Partenaires ({partners.length})</h2>
          </div>
          {partners.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Nom</th>
                  <th>Lien de redirection</th>
                  <th>Position</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={partner.id}>
                    <td>
                      <img
                        src={partner.logoUrl}
                        alt={partner.name}
                        style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px' }}
                      />
                    </td>
                    <td style={{ fontWeight: '600' }}>{partner.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#a0aec0' }}>
                      {partner.redirectUrl || '-'}
                    </td>
                    <td>{partner.position}</td>
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: partner.active ? '#48bb7820' : '#71809620',
                          color: partner.active ? '#48bb78' : '#718096',
                        }}
                      >
                        {partner.active ? '✅ Actif' : '⏸️ Inactif'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleOpenModal(partner)}
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #2d3748',
                            background: '#2d3748',
                            color: 'white',
                            cursor: 'pointer',
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(partner.id)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #fc8181',
                            background: '#fc818120',
                            color: '#fc8181',
                            cursor: 'pointer',
                            fontSize: '18px',
                            lineHeight: '1',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              Aucun partenaire. Cliquez sur "Ajouter un partenaire" pour commencer.
            </div>
          )}
        </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#1a202c',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingPartner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Nom du partenaire:
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Agence XYZ"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Logo du partenaire:
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setLogoFile(file);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '14px',
                }}
              />
              {(formData.logoUrl || logoFile) && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <img
                    src={logoFile ? URL.createObjectURL(logoFile) : formData.logoUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #2d3748',
                      padding: '8px',
                      background: '#0d1117',
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Lien de redirection (optionnel):
              </label>
              <input
                type="text"
                value={formData.redirectUrl}
                onChange={(e) => setFormData({ ...formData, redirectUrl: e.target.value })}
                placeholder="https://example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px', display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Position:
                </label>
                <input
                  type="number"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    background: '#0d1117',
                    color: 'white',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Statut:
                </label>
                <select
                  value={formData.active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    background: '#0d1117',
                    color: 'white',
                    fontSize: '14px',
                  }}
                >
                  <option value="active">✅ Actif</option>
                  <option value="inactive">⏸️ Inactif</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.name || (!logoFile && !formData.logoUrl) || uploading}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background:
                    formData.name && (logoFile || formData.logoUrl) && !uploading
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#2d3748',
                  color: 'white',
                  cursor: formData.name && (logoFile || formData.logoUrl) && !uploading ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '14px',
                }}
              >
                {uploading ? 'Upload en cours...' : editingPartner ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Partners;
